/**
 * WorkflowDesignerPage
 *
 * Create / Edit a Workflow Definition with its Approval Levels.
 * Routes:
 *   /setup/workflows/new        → creates new definition
 *   /setup/workflows/:id        → edits existing definition
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Plus, Trash2, Power, PowerOff, Star,
  GitBranch, CheckCircle, AlertCircle, ChevronUp, ChevronDown,
} from 'lucide-react';

import { workflowApi }        from '@/api/workflow.api';
import { transactionTypeApi } from '@/api/transactionType.api';
import { usePermission }      from '@/hooks/usePermission';
import { Permissions }        from '@/constants/permissions';
import { AppRoutes }          from '@/constants/routes';
import { Input } from '@/components/ui/Input';
import { Combobox } from '@/components/ui/Combobox';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import { Spinner }            from '@/components/ui/Spinner';
import { cn }                 from '@/utils/cn';
import type {
  WorkflowDefinitionDto,
  WorkflowLevelDto,
  AddWorkflowLevelRequest,
  ApproverType,
} from '@/types/workflow.types';
import type { TransactionTypeListDto } from '@/types/transactionType.types';
import { RoleLookup, UserLookup } from '@/components/lookups/EntityLookups';

// ── Field helpers ─────────────────────────────────────────────────────────────

// ── Level Row State (mirrors AddWorkflowLevelRequest fully) ───────────────────

interface LevelEditorRow {
  _id:                      string; // local key only
  levelNumber:              number;
  levelName:                string;
  approverType:             ApproverType;
  approverRoleId:           string;
  approverUserId:           string;
  minAmount:                string;
  maxAmount:                string;
  currencyCode:             string;
  mandatoryRemarks:         boolean;
  allowSendBack:            boolean;
  allowReject:              boolean;
  allowDelegate:            boolean;
  allowEscalation:          boolean;
  escalationHours:          string;
  escalationRoleId:         string;
  autoApprove:              boolean;
  autoPostAfterFinalApproval: boolean;
}

const mkLevelRow = (n: number): LevelEditorRow => ({
  _id:                      `new-${Date.now()}-${n}`,
  levelNumber:              n,
  levelName:                `Level ${n}`,
  approverType:             'Role',
  approverRoleId:           '',
  approverUserId:           '',
  minAmount:                '',
  maxAmount:                '',
  currencyCode:             '',
  mandatoryRemarks:         false,
  allowSendBack:            true,
  allowReject:              true,
  allowDelegate:            false,
  allowEscalation:          false,
  escalationHours:          '',
  escalationRoleId:         '',
  autoApprove:              false,
  autoPostAfterFinalApproval: false,
});

const dtoToRow = (l: WorkflowLevelDto): LevelEditorRow => ({
  _id:                      l.id,
  levelNumber:              l.levelNumber,
  levelName:                l.levelName,
  approverType:             l.approverType,
  approverRoleId:           l.approverRoleId ?? '',
  approverUserId:           l.approverUserId ?? '',
  minAmount:                l.minAmount != null ? String(l.minAmount) : '',
  maxAmount:                l.maxAmount != null ? String(l.maxAmount) : '',
  currencyCode:             l.currencyCode ?? '',
  mandatoryRemarks:         l.mandatoryRemarks,
  allowSendBack:            l.allowSendBack,
  allowReject:              l.allowReject,
  allowDelegate:            l.allowDelegate,
  allowEscalation:          l.allowEscalation,
  escalationHours:          l.escalationHours != null ? String(l.escalationHours) : '',
  escalationRoleId:         l.escalationRoleId ?? '',
  autoApprove:              l.autoApprove,
  autoPostAfterFinalApproval: l.autoPostAfterFinalApproval,
});

const rowToPayload = (row: LevelEditorRow): AddWorkflowLevelRequest => ({
  levelNumber:              row.levelNumber,
  levelName:                row.levelName,
  approverType:             row.approverType,
  approverRoleId:           row.approverRoleId || null,
  approverUserId:           row.approverUserId || null,
  minAmount:                row.minAmount ? parseFloat(row.minAmount) : null,
  maxAmount:                row.maxAmount ? parseFloat(row.maxAmount) : null,
  currencyCode:             row.currencyCode || null,
  locationId:               null,
  costCenterId:             null,
  mandatoryRemarks:         row.mandatoryRemarks,
  allowSendBack:            row.allowSendBack,
  allowReject:              row.allowReject,
  allowDelegate:            row.allowDelegate,
  allowEscalation:          row.allowEscalation,
  escalationHours:          row.escalationHours ? parseInt(row.escalationHours, 10) : null,
  escalationRoleId:         row.escalationRoleId || null,
  autoApprove:              row.autoApprove,
  autoPostAfterFinalApproval: row.autoPostAfterFinalApproval,
});

// ── Main Component ────────────────────────────────────────────────────────────

export const WorkflowDesignerPage = () => {
  const { id }    = useParams<{ id: string }>();
  const navigate  = useNavigate();
  const isNew     = !id;

  const { hasPermission } = usePermission();
  const canEdit   = hasPermission(Permissions.CFG_WORKFLOW_EDIT);
  const canCreate = hasPermission(Permissions.CFG_WORKFLOW_CREATE);
  const editable  = isNew ? canCreate : canEdit;

  // ── Reference data ────────────────────────────────────────────────────────
  const [txTypes, setTxTypes] = useState<TransactionTypeListDto[]>([]);
  useEffect(() => {
    transactionTypeApi.getList({ pageSize: 100 })
      .then(r => setTxTypes(r.data.data?.items ?? []))
      .catch(() => {});
  }, []);

  // ── Form state ────────────────────────────────────────────────────────────
  const [definition, setDefinition] = useState<WorkflowDefinitionDto | null>(null);
  const [loading,    setLoading]    = useState(!isNew);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [success,    setSuccess]    = useState<string | null>(null);

  // Header fields
  const [code,          setCode]          = useState('');
  const [name,          setName]          = useState('');
  const [description,   setDescription]   = useState('');
  const [module,        setModule]        = useState('GL');
  const [txTypeCode,    setTxTypeCode]    = useState('JV');
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().split('T')[0]);
  const [effectiveTo,   setEffectiveTo]   = useState('');

  // Levels
  const [levels, setLevels] = useState<LevelEditorRow[]>([]);

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (isNew || !id) return;
    setLoading(true);
    try {
      const res = await workflowApi.getById(id);
      const d = res.data.data;
      if (!d) throw new Error('Not found');
      setDefinition(d);
      setCode(d.code);
      setName(d.name);
      setDescription(d.description ?? '');
      setModule(d.module ?? 'GL');
      setTxTypeCode(d.transactionTypeCode);
      setEffectiveFrom(d.effectiveFrom ? d.effectiveFrom.split('T')[0] : '');
      setEffectiveTo(d.effectiveTo ? d.effectiveTo.split('T')[0] : '');
      setLevels(d.levels.map(dtoToRow));
    } catch {
      setError('Failed to load workflow definition.');
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  // ── Save header ───────────────────────────────────────────────────────────
  const handleSave = async () => {
    setError(null);
    if (!code.trim() || !name.trim()) {
      setError('Code and Name are required.');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const res = await workflowApi.create({
          code:                code.trim().toUpperCase(),
          name:                name.trim(),
          description:         description.trim() || null,
          module:              module || 'GL',
          transactionTypeCode: txTypeCode,
          effectiveFrom:       effectiveFrom || new Date().toISOString().split('T')[0],
          effectiveTo:         effectiveTo   || null,
        });
        const newId = res.data.data?.id;
        setSuccess('Workflow created. You can now add approval levels.');
        setTimeout(() => navigate(AppRoutes.CFG_WORKFLOW_DETAIL.replace(':id', newId!)), 1000);
      } else if (definition) {
        await workflowApi.update(definition.id, {
          name:         name.trim(),
          description:  description.trim() || null,
          effectiveFrom: effectiveFrom || null,
          effectiveTo:   effectiveTo   || null,
        });
        setSuccess('Workflow updated.');
        setTimeout(() => { setSuccess(null); load(); }, 1000);
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message ?? 'Save failed.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  // ── Add level ─────────────────────────────────────────────────────────────
  const handleAddLevel = async (row: LevelEditorRow) => {
    if (!definition) return;
    setSaving(true);
    try {
      await workflowApi.addLevel(definition.id, rowToPayload(row));
      setSuccess('Level added.');
      setTimeout(() => { setSuccess(null); load(); }, 800);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message ?? 'Failed to add level.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  // ── Delete level ──────────────────────────────────────────────────────────
  const handleDeleteLevel = async (levelId: string, levelNumber: number) => {
    if (!definition) return;
    if (!confirm(`Remove Level ${levelNumber}?`)) return;
    setSaving(true);
    try {
      await workflowApi.deleteLevel(definition.id, levelId);
      setSuccess('Level removed.');
      setTimeout(() => { setSuccess(null); load(); }, 800);
    } catch {
      setError('Failed to remove level.');
    } finally {
      setSaving(false);
    }
  };

  // ── Activate / Deactivate ─────────────────────────────────────────────────
  const handleActivate = async () => {
    if (!definition) return;
    const setDefault = confirm('Set this workflow as the default for its transaction type?');
    setSaving(true);
    try {
      await workflowApi.activate(definition.id, setDefault);
      setSuccess('Workflow activated.');
      setTimeout(() => { setSuccess(null); load(); }, 800);
    } catch {
      setError('Activate failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!definition) return;
    if (!confirm('Deactivate this workflow?')) return;
    setSaving(true);
    try {
      await workflowApi.deactivate(definition.id);
      setSuccess('Workflow deactivated.');
      setTimeout(() => { setSuccess(null); load(); }, 800);
    } catch {
      setError('Deactivate failed.');
    } finally {
      setSaving(false);
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900 overflow-hidden">

      {/* ── Toolbar ──────────────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-2.5">
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="ghost" size="sm" leftIcon={<ArrowLeft size={14} />}
            onClick={() => navigate(AppRoutes.CFG_WORKFLOWS)}>
            Back
          </Button>

          <div className="w-px h-6 bg-slate-200 dark:bg-slate-600" />

          {editable && (
            <Button size="sm" onClick={handleSave} loading={saving}
              leftIcon={<Save size={14} />}>
              {isNew ? 'Create Workflow' : 'Save Changes'}
            </Button>
          )}

          {!isNew && definition && canEdit && (
            definition.isActive ? (
              <Button variant="warning" size="sm" onClick={handleDeactivate} disabled={saving}
                leftIcon={<PowerOff size={14} />}>
                Deactivate
              </Button>
            ) : (
              <Button variant="success" size="sm" onClick={handleActivate} disabled={saving}
                leftIcon={<Power size={14} />}>
                Activate
              </Button>
            )
          )}

          {/* Status badges */}
          {!isNew && definition && (
            <div className="ml-auto flex items-center gap-2">
              {definition.isDefault && (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold
                                 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                  <Star size={10} fill="currentColor" /> Default
                </span>
              )}
              <span className={cn(
                'px-2 py-0.5 rounded-full text-xs font-semibold',
                definition.isActive ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-slate-200 text-slate-500 dark:text-gray-400',
              )}>
                {definition.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          )}
        </div>

        {/* Alerts */}
        {error && (
          <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20
                          border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-400">
            <AlertCircle size={13} className="shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-green-50 dark:bg-green-900/20
                          border border-green-200 dark:border-green-800 text-sm text-green-700 dark:text-green-400">
            <CheckCircle size={13} className="shrink-0" />
            <span>{success}</span>
          </div>
        )}
      </div>

      {/* ── Body ──────────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto p-6 space-y-6">

        {/* ── Header Card ───────────────────────────────────────────────────── */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
          <div className="flex items-center gap-2 mb-4">
            <GitBranch size={16} className="text-blue-600 dark:text-blue-400" />
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              {isNew ? 'New Workflow Definition' : 'Workflow Definition'}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

            {/* Code */}
            <div>
              <Input label="Code *" size="xs" value={code} onChange={e => setCode(e.target.value.toUpperCase())} disabled={!editable || !isNew} placeholder="e.g. WF-JV-STD" className="font-mono" />
            </div>

            {/* Name */}
            <div className="sm:col-span-2">
              <Input label="Name *" size="xs" value={name} onChange={e => setName(e.target.value)} disabled={!editable} placeholder="e.g. Standard Journal Approval" />
            </div>

            {/* Module */}
            <div>
              <Combobox
                label="Module *"
                size="xs"
                options={[{ value: "GL", label: "General Ledger" }, { value: "AR", label: "Accounts Receivable" }, { value: "AP", label: "Accounts Payable" }, { value: "INV", label: "Inventory" }, { value: "PAY", label: "Payroll" }]}
                value={module}
                onChange={setModule}
                disabled={!editable || !isNew}
                clearable={false}
              />
            </div>

            {/* Transaction Type */}
            <div>
              <Combobox
                label="Transaction Type *"
                size="xs"
                options={txTypes.map(o => ({ value: o.typeCode, label: `${o.typeCode} — ${o.description}` }))}
                value={txTypeCode}
                onChange={setTxTypeCode}
                disabled={!editable || !isNew}
                clearable={false}
              />
            </div>

            {/* Effective From */}
            <div>
              <Input label="Effective From" size="xs" type="date" value={effectiveFrom} onChange={e => setEffectiveFrom(e.target.value)} disabled={!editable} />
            </div>

            {/* Effective To */}
            <div>
              <Input label="Effective To" size="xs" type="date" value={effectiveTo} onChange={e => setEffectiveTo(e.target.value)} disabled={!editable} />
            </div>

            {/* Description */}
            <div className="sm:col-span-2 lg:col-span-3">
              <Input label="Description" size="xs" value={description} onChange={e => setDescription(e.target.value)} disabled={!editable} placeholder="Describe when this workflow applies…" />
            </div>
          </div>
        </div>

        {/* ── Approval Levels ────────────────────────────────────────────────── */}
        {!isNew && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center text-[10px] font-bold">
                  {levels.length}
                </span>
                Approval Levels
              </h2>
              {editable && (
                <Button
                  variant="secondary"
                  size="xs"
                  leftIcon={<Plus size={12} />}
                  onClick={() => setLevels(prev => [...prev, mkLevelRow(levels.length + 1)])}
                >
                  Add Level
                </Button>
              )}
            </div>

            {levels.length === 0 ? (
              <div className="flex flex-col items-center py-8 text-slate-400">
                <GitBranch size={28} className="mb-2 opacity-30" />
                <p className="text-sm">No approval levels defined.</p>
                {editable && (
                  <p className="text-xs mt-1">Click "Add Level" to define the first approval step.</p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {levels.map((row) => (
                  <LevelRowEditor
                    key={row._id}
                    row={row}
                    isNew={!definition?.levels.find(l => l.id === row._id)}
                    editable={editable}
                    onChange={patch => setLevels(prev =>
                      prev.map(r => r._id === row._id ? { ...r, ...patch } : r)
                    )}
                    onSave={() => handleAddLevel(row)}
                    onDelete={() => {
                      const existing = definition?.levels.find(l => l.id === row._id);
                      if (existing) {
                        handleDeleteLevel(existing.id, existing.levelNumber);
                      } else {
                        setLevels(prev => prev.filter(r => r._id !== row._id));
                      }
                    }}
                    saving={saving}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Workflow Preview ─────────────────────────────────────────────── */}
        {!isNew && levels.length > 0 && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-4">
              Approval Flow Preview
            </h2>
            <div className="flex items-start gap-0 overflow-x-auto pb-2">
              {/* Start */}
              <div className="flex flex-col items-center flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                  <GitBranch size={16} className="text-slate-500 dark:text-gray-400" />
                </div>
                <span className="text-[10px] text-slate-400 mt-1">Start</span>
              </div>

              {levels.map((l) => (
                <div key={l._id} className="flex items-start">
                  {/* Arrow */}
                  <div className="flex items-center h-10">
                    <div className="w-8 h-px bg-slate-300 dark:bg-slate-600" />
                    <div className="w-0 h-0 border-t-[5px] border-b-[5px] border-l-[6px]
                                    border-t-transparent border-b-transparent border-l-slate-300 dark:border-l-slate-600" />
                  </div>
                  {/* Level box */}
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className={cn(
                      'w-24 rounded-lg border-2 p-2 text-center',
                      'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/30',
                    )}>
                      <p className="text-[9px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wide">
                        Level {l.levelNumber}
                      </p>
                      <p className="text-[10px] text-slate-700 dark:text-slate-300 mt-0.5 leading-tight font-medium">
                        {l.levelName}
                      </p>
                      <p className="text-[9px] text-slate-400 mt-0.5">
                        {l.approverType === 'Role' ? 'By Role' : 'By User'}
                      </p>
                      {l.minAmount && (
                        <p className="text-[9px] text-amber-600 dark:text-amber-400 mt-0.5">
                          &gt;{Number(l.minAmount).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Arrow to end */}
              <div className="flex items-start">
                <div className="flex items-center h-10">
                  <div className="w-8 h-px bg-slate-300 dark:bg-slate-600" />
                  <div className="w-0 h-0 border-t-[5px] border-b-[5px] border-l-[6px]
                                  border-t-transparent border-b-transparent border-l-slate-300 dark:border-l-slate-600" />
                </div>
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                    <CheckCircle size={16} className="text-emerald-500" />
                  </div>
                  <span className="text-[10px] text-slate-400 mt-1">Approved</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Level Row Editor Component ────────────────────────────────────────────────

interface LevelRowEditorProps {
  row:       LevelEditorRow;
  isNew:     boolean;
  editable:  boolean;
  onChange:  (patch: Partial<LevelEditorRow>) => void;
  onSave:    () => void;
  onDelete:  () => void;
  saving:    boolean;
}

/**
 * A compact inline checkbox for the designer's dense option rows.
 *
 * Named for what it is. It used to be called `Toggle`, which shadowed the kit's Toggle — a switch,
 * not a checkbox — so the name promised one affordance and drew another. Left as a checkbox
 * deliberately: these sit several to a row in a grid where the switch's height would not fit.
 */
const InlineCheckbox = ({
  label, checked, onChange, disabled,
}: { label: string; checked: boolean; onChange: (v: boolean) => void; disabled: boolean }) => (
  <label className="flex items-center gap-2 cursor-pointer select-none">
    <input
      type="checkbox"
      checked={checked}
      onChange={e => onChange(e.target.checked)}
      disabled={disabled}
      className="w-4 h-4 rounded text-blue-600 accent-blue-600"
    />
    <span className="text-xs text-slate-700 dark:text-slate-300">{label}</span>
  </label>
);

const LevelRowEditor = ({
  row, isNew, editable, onChange, onSave, onDelete, saving,
}: LevelRowEditorProps) => {
  const [expanded, setExpanded] = useState(isNew);
  const dis = !editable || !isNew;

  return (
    <div className={cn(
      'rounded-lg border transition-all',
      isNew
        ? 'border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10'
        : 'border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50',
    )}>
      {/* Row header */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
        onClick={() => setExpanded(e => !e)}
      >
        <div className={cn(
          'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0',
          isNew ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300',
        )}>
          {row.levelNumber}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{row.levelName}</p>
          <p className="text-xs text-slate-400 truncate">
            {row.approverType === 'Role' ? 'By Role' : 'By User'}
            {row.minAmount && ` · >${Number(row.minAmount).toLocaleString()}`}
            {row.maxAmount && ` · ≤${Number(row.maxAmount).toLocaleString()}`}
            {row.allowEscalation && row.escalationHours && ` · Escalates after ${row.escalationHours}h`}
          </p>
        </div>
        {isNew && (
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex-shrink-0">
            Unsaved
          </span>
        )}
        <div className="flex items-center gap-1">
          {editable && isNew && (
            <IconButton
              label="Save level"
              variant="bare"
              size="xs"
              loading={saving}
              icon={<CheckCircle size={14} />}
              onClick={e => { e.stopPropagation(); onSave(); }}
              className="text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20"
            />
          )}
          {editable && (
            <IconButton
              label="Delete level"
              variant="bare"
              size="xs"
              disabled={saving}
              icon={<Trash2 size={13} />}
              onClick={e => { e.stopPropagation(); onDelete(); }}
              className="hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
            />
          )}
          {expanded
            ? <ChevronUp   size={14} className="text-slate-400 flex-shrink-0" />
            : <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />}
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-100 dark:border-slate-700 pt-3 space-y-4">

          {/* Row 1: core identification */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {/* Level Name */}
            <div>
              <Input label="Level Name" size="xs" value={row.levelName} onChange={e => onChange({ levelName: e.target.value })} disabled={dis} />
            </div>

            {/* Approver Type */}
            <div>
              <Combobox label="Approver Type" size="xs" clearable={false}
                options={[{ value: "Role", label: "By Role" }, { value: "User", label: "By User" }]}
                value={row.approverType}
                onChange={v => onChange({ approverType: v as ApproverType })}
                disabled={dis}
              />
            </div>

            {/* Who approves at this level — a role, or one named person */}
            <div>
              {row.approverType === 'Role' ? (
                <RoleLookup
                  label="Approving role"
                  value={row.approverRoleId}
                  onChange={v => onChange({ approverRoleId: v ?? '' })}
                  disabled={dis}
                />
              ) : (
                <UserLookup
                  label="Approver"
                  value={row.approverUserId}
                  onChange={v => onChange({ approverUserId: v ?? '' })}
                  disabled={dis}
                />
              )}
            </div>
          </div>

          {/* Row 2: amount conditions */}
          <div>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-2">
              Amount Condition (optional — leave blank for any amount)
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Input label="Min Amount" size="xs" type="number" value={row.minAmount} onChange={e => onChange({ minAmount: e.target.value })} disabled={dis} placeholder="0.00" />
              </div>
              <div>
                <Input label="Max Amount" size="xs" type="number" value={row.maxAmount} onChange={e => onChange({ maxAmount: e.target.value })} disabled={dis} placeholder="Unlimited" />
              </div>
              <div>
                <Input label="Currency" size="xs" value={row.currencyCode} onChange={e => onChange({ currencyCode: e.target.value.toUpperCase() })} disabled={dis} placeholder="e.g. USD" maxLength={3} className="font-mono uppercase" />
              </div>
            </div>
          </div>

          {/* Row 3: action permissions */}
          <div>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-2">
              Approver Permissions
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              <InlineCheckbox label="Mandatory Remarks" checked={row.mandatoryRemarks} onChange={v => onChange({ mandatoryRemarks: v })} disabled={dis} />
              <InlineCheckbox label="Allow Send Back"   checked={row.allowSendBack}    onChange={v => onChange({ allowSendBack: v })}    disabled={dis} />
              <InlineCheckbox label="Allow Reject"      checked={row.allowReject}      onChange={v => onChange({ allowReject: v })}      disabled={dis} />
              <InlineCheckbox label="Allow Delegation"  checked={row.allowDelegate}    onChange={v => onChange({ allowDelegate: v })}    disabled={dis} />
            </div>
          </div>

          {/* Row 4: escalation */}
          <div>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-2">
              Escalation
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <div className="flex items-center gap-3 pt-2">
                <InlineCheckbox label="Enable Escalation" checked={row.allowEscalation} onChange={v => onChange({ allowEscalation: v })} disabled={dis} />
              </div>
              {row.allowEscalation && (
                <>
                  <div>
                    <Input label="After (hours)" size="xs" type="number" value={row.escalationHours} onChange={e => onChange({ escalationHours: e.target.value })} disabled={dis} placeholder="e.g. 48" />
                  </div>
                  <div>
                    <RoleLookup
                      label="Escalate to"
                      value={row.escalationRoleId}
                      onChange={v => onChange({ escalationRoleId: v ?? '' })}
                      disabled={dis}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Row 5: automation */}
          <div>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-2">
              Automation
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              <InlineCheckbox label="Auto-Approve"                   checked={row.autoApprove}              onChange={v => onChange({ autoApprove: v })}              disabled={dis} />
              <InlineCheckbox label="Auto-Post After Final Approval" checked={row.autoPostAfterFinalApproval} onChange={v => onChange({ autoPostAfterFinalApproval: v })} disabled={dis} />
            </div>
          </div>

        </div>
      )}
    </div>
  );
};
